// Client-side mirror of SelectionCategory_Upsert's generic skip-level algorithm (see that
// stored procedure's comments for the full rationale/derivation). This exists purely for live UX
// -- which dropdown is enabled, what level/parent a new tree node should get -- the backend SP is
// still the sole enforcing authority and will reject anything inconsistent with this.
//
// Levels 1 (Main Category) and 2 (Category) are always fixed/structural. Levels 3+ are
// per-Main-Category configurable (SelectionCategoryLevelConfig); a Main Category with zero config
// rows is either brand new (valid, simply stops at Category) or "legacy" -- it predates level
// config and already has real Level 3/4/5 Type/Brand/Range nodes that must keep working untouched.

/** Map<levelNumber, {id, levelNumber, alias, isOptional}> from a level-config list. */
export function buildLevelConfigMap(levels) {
  const map = new Map();
  (levels || []).forEach((l) => map.set(l.levelNumber, l));
  return map;
}

/**
 * The set of levels a node at `level` (>= 3) may legally have as its parent, given this Main
 * Category's level-config map. Generalizes "Type is optional, so Brand can attach to Category or
 * Type" to arbitrary depth: walk up from level-1, and keep walking through a contiguous run of
 * levels marked optional, stopping at the first non-optional level (or at Level 2).
 */
export function validParentLevels(level, levelConfigMap) {
  if (level < 3) return [];
  const set = [];
  let cur = level - 1;
  set.push(cur);
  while (cur > 1 && (cur === 2 || levelConfigMap.get(cur)?.isOptional)) {
    cur -= 1;
    set.push(cur);
  }
  return set;
}

/** Walks a flat node list up from `nodeId` to find its ancestor at exactly `targetLevel`. */
export function findAncestorAtLevel(flatNodes, nodeId, targetLevel) {
  let cur = (flatNodes || []).find((n) => n.id === nodeId);
  const visited = new Set();
  while (cur && !visited.has(cur.id)) {
    if (cur.level === targetLevel) return cur;
    visited.add(cur.id);
    cur = flatNodes.find((n) => n.id === cur.parentNodeId);
  }
  return null;
}

/** Walks up to the Level-1 (Main Category) ancestor of any node, or itself if it's already one. */
export function findMainCategoryNode(flatNodes, nodeId) {
  const node = (flatNodes || []).find((n) => n.id === nodeId);
  if (!node) return null;
  if (node.level === 1) return node;
  return findAncestorAtLevel(flatNodes, nodeId, 1);
}

/**
 * True if a Main Category has real Level 3+ nodes beneath it even though it has no level-config
 * rows -- i.e. it predates the config system (SelectionCategory_Upsert's "legacy fallback").
 */
export function hasLegacyDescendants(flatNodes, mainCategoryNodeId) {
  if (!mainCategoryNodeId) return false;
  return (flatNodes || []).some(
    (n) => n.level >= 3 && findAncestorAtLevel(flatNodes, n.id, 1)?.id === mainCategoryNodeId
  );
}

/**
 * Resolves what adding a child under `node` should mean: the next level number, whether it's
 * allowed at all, and whether we're in "legacy mode" (old hardcoded Type/Brand/Range rules, no
 * alias available) vs. "configured mode" (alias comes from level-config).
 *
 * Used by both the tree "+" affordance (SelectionsMasterTemplateBuilder / HierarchyTreeNavigator)
 * and CategoryHierarchyPicker to decide what's addable next.
 */
export function resolveAddChildTarget(node, flatNodes, levelConfigRows) {
  if (!node) return { allowed: false };

  if (node.level === 1) {
    // Category (Level 2) is always addable under a Main Category -- fixed/structural.
    return { allowed: true, nextLevel: 2, parentId: node.id, alias: "Category", isLegacy: false };
  }

  const mainCategoryNode = node.level === 1 ? node : findMainCategoryNode(flatNodes, node.id);
  if (!mainCategoryNode) return { allowed: false };

  const configRows = levelConfigRows || [];
  const configMap = buildLevelConfigMap(configRows);
  const nextLevel = node.level + 1;

  if (configRows.length === 0) {
    // Zero config: legacy (has real deeper data) or brand new. Either way, the old hardcoded
    // Type(3)/Brand(4)/Range(5) shape is what SelectionCategory_Upsert will accept for this Main
    // Category until it's configured, so mirror that here, capped at Level 5.
    if (nextLevel > 5) {
      return {
        allowed: false,
        reason: "No levels are configured below Category for this Main Category yet. Use “Manage Levels” to add one.",
      };
    }
    return { allowed: true, nextLevel, parentId: node.id, alias: null, isLegacy: true };
  }

  const maxConfigured = Math.max(...configRows.map((l) => l.levelNumber));
  if (nextLevel > maxConfigured) {
    return {
      allowed: false,
      reason: "This is the deepest configured level for this Main Category. Use “Manage Levels” to add another.",
    };
  }

  const levelConfig = configMap.get(nextLevel);
  return {
    allowed: true,
    nextLevel,
    parentId: node.id,
    alias: levelConfig?.alias || `Level ${nextLevel}`,
    isLegacy: false,
  };
}

const sameName = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();

/**
 * Pure resolution of a Main Category > Category > ...levels... string path into actual nodes,
 * shared by CategoryHierarchyPicker (for rendering/dropdown filtering) and any caller that needs
 * the authoritative deepest node id (e.g. AddSelectionModal at submit time) -- one algorithm, no
 * duplicated logic between the two.
 *
 * `levelValues` is positional: index 0 is the first configured/legacy level below Category, index
 * 1 the next, etc. (matches `effectiveLevels`' order, ascending by levelNumber). A level's parent
 * is the deepest already-resolved node among its valid-parent levels (see validParentLevels) --
 * this is what lets an optional level be left blank while the level below it still resolves
 * against a shallower ancestor, falling all the way back to the Category node if every level
 * above it was left blank.
 */
export function resolveCategoryPath({ flatNodes, effectiveLevels, mainCategory, category, levelValues }) {
  const nodes = flatNodes || [];
  const mainNode = nodes.find((n) => n.level === 1 && sameName(n.name, mainCategory)) || null;
  const catNode = mainNode
    ? nodes.find((n) => n.level === 2 && n.parentNodeId === mainNode.id && sameName(n.name, category)) || null
    : null;

  const levelConfigMap = buildLevelConfigMap(effectiveLevels);
  const resolvedLevels = [];
  const nodeAtLevel = (levelNumber) => {
    if (levelNumber === 1) return mainNode;
    if (levelNumber === 2) return catNode;
    const entry = resolvedLevels.find((r) => r.levelNumber === levelNumber);
    return entry ? entry.selectedNode : null;
  };

  (effectiveLevels || []).forEach((lvl, idx) => {
    const parents = validParentLevels(lvl.levelNumber, levelConfigMap);
    let parentNode = null;
    for (const p of [...parents].sort((a, b) => b - a)) {
      const n = nodeAtLevel(p);
      if (n) {
        parentNode = n;
        break;
      }
    }
    if (!parentNode) parentNode = catNode || mainNode;

    const rawValue = ((levelValues || [])[idx] || "").trim();
    let selectedNode = null;
    if (rawValue) {
      if (parentNode && parentNode.id !== mainNode?.id) {
        selectedNode = nodes.find(
          (n) => n.level === lvl.levelNumber && n.parentNodeId === parentNode.id && sameName(n.name, rawValue)
        ) || null;
      }
      if (!selectedNode && !catNode && mainNode) {
        selectedNode = nodes.find((n) => {
          if (n.level !== lvl.levelNumber || !sameName(n.name, rawValue)) return false;
          if (n.parentNodeId === mainNode.id) return true;
          const p = nodes.find((parent) => parent.id === n.parentNodeId);
          return p && (p.parentNodeId === mainNode.id || p.id === mainNode.id);
        }) || null;
      }
    }

    resolvedLevels.push({
      id: lvl.id,
      levelNumber: lvl.levelNumber,
      alias: lvl.alias,
      isOptional: !!lvl.isOptional,
      parentNode,
      selectedNode,
      name: rawValue,
    });
  });

  let deepestNodeId = null;
  for (let i = resolvedLevels.length - 1; i >= 0; i -= 1) {
    if (resolvedLevels[i].selectedNode) {
      deepestNodeId = resolvedLevels[i].selectedNode.id;
      break;
    }
  }
  if (!deepestNodeId) deepestNodeId = catNode?.id || mainNode?.id || null;

  return {
    mainNode,
    catNode,
    resolvedLevels,
    mainCategoryNodeId: mainNode?.id || null,
    categoryNodeId: deepestNodeId,
  };
}

/**
 * Walks a flat node list from a leaf id up to the root, returning the ancestor chain
 * root-to-leaf (inclusive of the leaf itself). Ported from the old dynamicHierarchyConfig.js.
 */
export function resolveAncestorChain(flatNodes, nodeId) {
  if (!flatNodes || !nodeId) return [];
  const chain = [];
  let curr = flatNodes.find((n) => n.id === nodeId);
  const visited = new Set();
  while (curr && !visited.has(curr.id)) {
    visited.add(curr.id);
    chain.unshift(curr);
    curr = flatNodes.find((n) => n.id === curr.parentNodeId);
  }
  return chain;
}

/**
 * Resolves the display value of a specific ancestor level (by level NUMBER) for an item that
 * carries a categoryNodeId -- e.g. "what's this option's Level-4 ancestor's name". Replaces the
 * old dynamicHierarchyConfig.js's multi-source-fallback getItemDynamicLevelValue: a node's own
 * ancestor chain is the one reliable source of truth now that levels are backend-driven.
 */
export function resolveItemLevelValue(item, levelNumber, flatNodes) {
  if (!item || !levelNumber) return "";
  const categoryNodeId = item.categoryNodeId || item.CategoryNodeId;
  if (!categoryNodeId || !flatNodes?.length) return "";
  const chain = resolveAncestorChain(flatNodes, categoryNodeId);
  const node = chain.find((n) => n.level === levelNumber);
  return node?.name || "";
}

/**
 * Normalizes SelectionCategoryLevelConfig rows ({id, levelNumber, alias, isOptional}) into the
 * {id, name, level, key} shape the Builder tab's dynamic filter-row UI already expects, so moving
 * the data source from localStorage to the backend doesn't ripple through every consumer.
 */
export function toLegacyDynamicLevelsShape(configRows) {
  return (configRows || []).map((l) => ({
    id: l.id,
    name: l.alias,
    level: l.levelNumber,
    key: `dynamic_${(l.alias || "").toLowerCase().replace(/[^a-z0-9]+/g, "_")}_${l.id}`,
  }));
}

/**
 * The levels to show for a Main Category's filter UI: real config rows if any exist, otherwise
 * (for a "legacy" Main Category with zero config rows but real Level 3+ data already present)
 * synthesized read-only rows for whichever of Type(3)/Brand(4)/Range(5) actually have at least one
 * node under this Main Category -- mirrors SelectionCategory_Upsert's legacy fallback. A brand-new,
 * never-configured, never-populated Main Category correctly yields [].
 */
export function buildEffectiveLevels(configRows, flatNodes, mainCategoryNodeId) {
  if (configRows && configRows.length > 0) return configRows;
  if (!hasLegacyDescendants(flatNodes, mainCategoryNodeId)) return [];

  const legacyLabels = { 3: "Type", 4: "Brand", 5: "Range" };
  return [3, 4, 5]
    .filter((lvl) =>
      (flatNodes || []).some(
        (n) => n.level === lvl && findAncestorAtLevel(flatNodes, n.id, 1)?.id === mainCategoryNodeId
      )
    )
    .map((lvl) => ({ id: `legacy-${mainCategoryNodeId}-${lvl}`, levelNumber: lvl, alias: legacyLabels[lvl], isOptional: lvl === 3 }));
}

/**
 * Returns true if `nodeId` is equal to or a descendant of `ancestorId` in `flatNodes`.
 */
export function isDescendantOf(flatNodes, nodeId, ancestorId) {
  if (!nodeId || !ancestorId) return false;
  if (nodeId === ancestorId) return true;
  let cur = (flatNodes || []).find((n) => n.id === nodeId);
  const visited = new Set();
  while (cur && !visited.has(cur.id)) {
    if (cur.parentNodeId === ancestorId) return true;
    visited.add(cur.id);
    cur = flatNodes.find((n) => n.id === cur.parentNodeId);
  }
  return false;
}

